<template>
  <div class="min-h-screen flex flex-col relative overflow-x-hidden bg-selk-bg text-selk-textMain">

    <!-- NAVBAR -->
    <nav class="fixed top-0 w-full z-50 bg-selk-bg/80 backdrop-blur-md border-b border-selk-card">
      <div class="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">

        <!-- Logo -->
        <div class="flex items-center gap-2 font-bold text-xl tracking-wide">
          <span class="w-8 h-8 rounded bg-gradient-to-br from-selk-accent to-selk-accentDark flex items-center justify-center text-white">
            M
          </span>
          <span>INDSET</span>
        </div>

        <!-- Language Switcher -->
        <div class="flex bg-selk-card rounded-full p-1 border border-slate-700/50">
          <button
            v-for="(label, code) in languages"
            :key="code"
            @click="setLanguage(code)"
            :class="[
              'px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300',
              currentLang === code
                ? 'bg-selk-accentDark text-white shadow-lg'
                : 'text-selk-textMuted hover:text-white hover:bg-white/5'
            ]"
          >
            {{ label }}
          </button>
        </div>

      </div>
    </nav>

    <!-- HERO -->
    <header class="pt-36 pb-20 px-6 flex flex-col items-center relative">

      <div class="text-center mb-16">
        <h1 class="text-5xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-selk-textMuted">
          {{ t.mindsetTitle }}
        </h1>
        <p class="text-selk-accent text-lg font-medium tracking-wider uppercase">
          {{ t.subtitle }}
        </p>
      </div>

      <!-- PROFILE + BUBBLES -->
      <div class="relative w-[280px] h-[280px] md:w-[400px] md:h-[400px] flex items-center justify-center mb-12">

        <!-- Profile -->
        <div class="relative z-10 w-48 h-48 md:w-64 md:h-64 rounded-full p-2 bg-gradient-to-br from-selk-accent to-selk-accentDark shadow-[0_0_40px_rgba(0,210,255,0.15)]">
          <img
            src="https://images.unsplash.com/photo-1556157382-97eda2d62296"
            class="w-full h-full object-cover rounded-full border-4 border-selk-bg"
            alt="Profile"
          />
        </div>

        <!-- Bubbles -->
        <a href="#mission" class="bubble top-0 left-1/2 -translate-x-1/2">
          🚀
        </a>

        <a href="#vision" class="bubble top-1/2 right-0 -translate-y-1/2">
          👁
        </a>

        <a href="#skills" class="bubble bottom-0 left-1/2 -translate-x-1/2">
          💻
        </a>

        <a href="#hobbies" class="bubble top-1/2 left-0 -translate-y-1/2">
          🎮
        </a>

      </div>
    </header>

    <!-- CONTENT -->
    <main class="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8 pb-32">

      <!-- Mission -->
      <section id="mission" class="card">
        <h2 class="title">{{ t.missionTitle }}</h2>
        <p class="text-selk-textMuted">{{ t.missionText }}</p>
      </section>

      <!-- Vision -->
      <section id="vision" class="card">
        <h2 class="title">{{ t.visionTitle }}</h2>
        <p class="text-selk-textMuted">{{ t.visionText }}</p>
      </section>

      <!-- Skills -->
      <section id="skills" class="card">
        <h2 class="title">{{ t.skillsTitle }}</h2>
        <p class="text-selk-textMuted mb-4">{{ t.skillsText }}</p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="skill in t.skillsList"
            :key="skill"
            class="tag"
          >
            {{ skill }}
          </span>
        </div>
      </section>

      <!-- Hobbies -->
      <section id="hobbies" class="card">
        <h2 class="title">{{ t.hobbiesTitle }}</h2>
        <p class="text-selk-textMuted mb-4">{{ t.hobbiesText }}</p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="hobby in t.hobbiesList"
            :key="hobby"
            class="tag text-white"
          >
            {{ hobby }}
          </span>
        </div>
      </section>

    </main>

  </div>
</template>

<script setup>
import { ref, computed } from "vue"

const currentLang = ref("es")

const languages = {
  es: "Español",
  en: "English",
  de: "Deutsch"
}

const messages = {
  en: {
    mindsetTitle: "The Mindset",
    subtitle: "Innovation, Technology & Growth",
    missionTitle: "My Mission",
    missionText: "To build scalable digital solutions.",
    visionTitle: "My Vision",
    visionText: "To drive technological evolution.",
    skillsTitle: "My Skills",
    skillsText: "Modern web development stack.",
    skillsList: ["Vue.js", "Node.js", "Tailwind", "UI/UX"],
    hobbiesTitle: "My Hobbies",
    hobbiesText: "Exploring beyond technology.",
    hobbiesList: ["Travel", "Photography", "Reading"]
  },
  es: {
    mindsetTitle: "La Mentalidad",
    subtitle: "Innovación, Tecnología y Crecimiento",
    missionTitle: "Mi Misión",
    missionText: "Construir soluciones digitales escalables.",
    visionTitle: "Mi Visión",
    visionText: "Impulsar la evolución tecnológica.",
    skillsTitle: "Mis Habilidades",
    skillsText: "Stack moderno de desarrollo web.",
    skillsList: ["Vue.js", "Node.js", "Tailwind", "UI/UX"],
    hobbiesTitle: "Mis Pasatiempos",
    hobbiesText: "Explorando más allá de la tecnología.",
    hobbiesList: ["Viajar", "Fotografía", "Lectura"]
  },
  de: {
    mindsetTitle: "Die Denkweise",
    subtitle: "Innovation, Technologie & Wachstum",
    missionTitle: "Meine Mission",
    missionText: "Skalierbare digitale Lösungen entwickeln.",
    visionTitle: "Meine Vision",
    visionText: "Technologische Entwicklung vorantreiben.",
    skillsTitle: "Meine Fähigkeiten",
    skillsText: "Moderner Web-Stack.",
    skillsList: ["Vue.js", "Node.js", "Tailwind", "UI/UX"],
    hobbiesTitle: "Meine Hobbys",
    hobbiesText: "Inspiration außerhalb der Technologie.",
    hobbiesList: ["Reisen", "Fotografie", "Lesen"]
  }
}

const setLanguage = (lang) => {
  currentLang.value = lang
  localStorage.setItem("lang", lang)
}

const t = computed(() => messages[currentLang.value])
</script>

<style scoped>
.card {
  @apply bg-selk-card/80 border border-slate-800 p-8 rounded-3xl transition-all duration-300 hover:border-selk-accentDark/50;
}

.title {
  @apply text-2xl font-bold mb-4;
}

.tag {
  @apply text-xs font-semibold px-3 py-1 bg-selk-bg border border-slate-700 rounded-full text-selk-accent;
}

.bubble {
  position: absolute;
  width: 60px;
  height: 60px;
  background: #151E32;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00D2FF;
  transition: all 0.3s ease;
}

.bubble:hover {
  transform: scale(1.15);
  background: #00D2FF;
  color: #0B1121;
}
</style>
